import {
  Container,
  Typography,
  Box,
  TableContainer,
  Table,
  Divider,
  TablePagination,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
} from "@mui/material";
import InfoPaper from "components/utils/InfoPaper";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";
import { getReports } from "apis";
import { IChild } from "types";
import TablePaginationLocalizationProvider from "components/utils/TablePaginationLocalizationProvider";
import { Visibility } from "@mui/icons-material";
import dayjs from "dayjs";
import ViewReportDialog from "components/ViewReportDialog";

// table columns
interface ColumnProps {
  id: string;
  label: string;
  minWidth: number;
  align?: "right" | "left" | "inherit" | "center" | "justify" | undefined;
  format?: (value: Date | number) => string | boolean;
}

const columns: ColumnProps[] = [
  { id: "child", label: "timeline.child", minWidth: 100 },
  { id: "date", label: "labels.date", minWidth: 100 },
  { id: "action", label: "labels.action", minWidth: 100 },
];

type Report = {
  id: number;
  child: IChild;
  path: string;
  createdAt: string;
  comment: string;
};

const Reports: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [reports, setReports] = useState<Report[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [item, setItem] = useState<Report>();
  const [openViewDialog, setOpenViewDialog] = useState(false);
  useEffect(() => {
    getReports().then((data) => {
      setReports(data);
    });
  }, []);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event:
      | React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
      | undefined,
  ) => {
    setRowsPerPage(+event?.target?.value!);
    setPage(0);
  };

  const handleClickView = (
    event: React.MouseEvent<HTMLElement> | undefined,
    item: Report,
  ) => {
    setItem(item);
    setOpenViewDialog(true);
  };

  const handleCloseView = useCallback(() => {
    setOpenViewDialog(false);
  }, [setOpenViewDialog]);

  return (
    <Container>
      <InfoPaper>
        <Box sx={{ width: "100%" }}>
          <Box display="flex" flexDirection="column" my={2}>
            <Typography fontWeight="bold">{t("report.label")}</Typography>
          </Box>
          <TableContainer sx={{ maxHeight: 430 }}>
            <Table stickyHeader aria-label="sticky table">
              <TableHead
                sx={{
                  "& th": {
                    borderTop: `1px solid ${theme.palette.divider}`,
                    borderBottom: `2px solid ${theme.palette.divider} !important`,
                  },
                }}
              >
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      sx={{
                        minWidth: column.minWidth,
                        position: "sticky !important",
                        fontWeight: "bold",
                      }}
                      key={column.id}
                      align={column.align}
                    >
                      {t(column.label)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {reports
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((report, index) => (
                    <TableRow sx={{ py: 3 }} hover key={report.id}>
                      <TableCell key="name">
                        {report.child.firstName} {report.child.lastName}
                      </TableCell>

                      <TableCell key="range">
                        {dayjs(report.createdAt).format("DD/MM/YYYY")}
                      </TableCell>

                      <TableCell key="action">
                        <IconButton
                          aria-label="edit"
                          size="small"
                          onClick={(event) => handleClickView(event, report)}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Divider />
          <TablePaginationLocalizationProvider>
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={reports.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TablePaginationLocalizationProvider>
        </Box>
      </InfoPaper>

      {openViewDialog && (
        <ViewReportDialog
          open={openViewDialog}
          onClose={handleCloseView}
          item={item as Report}
        />
      )}
    </Container>
  );
};

export default Reports;
